import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, WhatsappLogo, EnvelopeSimple, Note, PaperPlaneTilt,
  CheckCircle, Warning, ClockClockwise, FileText, UploadSimple, Paperclip,
  CurrencyInr, ArrowsClockwise,
} from "@phosphor-icons/react";
import { SERVICE_STAGE_LABELS } from "./Templates";
import { DocumentAttachmentList } from "@/components/DocumentAttachment";
import { formatActivityError, followUpResult } from "@/lib/formatActivityError";

const STATUS_LABELS = { "new": "New", contacted: "Contacted", initial: "Initial", fu1: "Follow-up 1", fu2: "Follow-up 2", fu3: "Follow-up 3", fu4: "Follow-up 4", fu5: "Follow-up 5", converted: "Converted", lost: "Lost", quotation_sent: "Quote Sent", qfu1: "Quote FU-1", qfu2: "Quote FU-2", qfu3: "Quote FU-3", qfu4: "Quote FU-4", qfu5: "Quote FU-5", payment_reminder: "Payment Reminder", pfu1: "Payment FU-1", pfu2: "Payment FU-2", pfu3: "Payment FU-3", pfu4: "Payment FU-4", pfu5: "Payment FU-5" };
const STATUS_COLORS = { "new": "#3B82F6", contacted: "#6B7280", initial: "#6366F1", fu1: "#F59E0B", fu2: "#F97316", fu3: "#EF4444", fu4: "#DC2626", fu5: "#991B1B", converted: "#10B981", lost: "#9CA3AF", quotation_sent: "#8B5CF6", qfu1: "#A78BFA", qfu2: "#8B5CF6", qfu3: "#7C3AED", qfu4: "#6D28D9", qfu5: "#4C1D95", payment_reminder: "#059669", pfu1: "#10B981", pfu2: "#059669", pfu3: "#047857", pfu4: "#065F46", pfu5: "#064E3B" };

const CONTRACT_TYPE_LABELS = {
  warranty: "Warranty",
  out_of_warranty: "Out of Warranty",
  amc: "AMC",
  out_of_amc: "Out of AMC",
  on_call: "On Call",
  register: "Register",
  other: "Other"
};

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [activities, setActivities] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("whatsapp");
  const [msg, setMsg] = useState("");
  const [subject, setSubject] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [onBehalfOf, setOnBehalfOf] = useState("");
  const [whatsAppConnected, setWhatsAppConnected] = useState(false);
  const [emailFollowupEnabled, setEmailFollowupEnabled] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [quoteChannel, setQuoteChannel] = useState("whatsapp");
  const [payChannel, setPayChannel] = useState("whatsapp");
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const [loadingContract, setLoadingContract] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoadingContract(true);
      const [a, b] = await Promise.all([api.get(`/contracts/${id}`), api.get(`/contracts/${id}/activities`)]);
      setContract(a.data);
      setActivities(b.data || []);
    } catch (err) {
      console.error("Failed to load contract:", err);
      toast.error(err.response?.data?.detail || "Failed to load service contract details");
    } finally {
      setLoadingContract(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    if (user?.role !== "salesperson") api.get("/users/salespeople?department=service").then(r => setSalespeople(r.data)).catch(() => { });
    api.get("/templates").then(r => setTemplates(r.data)).catch(() => { });
  }, [id, load, user]);

  useEffect(() => {
    if (contract) {
      const spId = contract.assigned_to || user.id;
      api.get(`/whatsapp/status?userId=${spId}`)
        .then(res => setWhatsAppConnected(res.data.status === "CONNECTED"))
        .catch(() => setWhatsAppConnected(false));
    }
  }, [contract, user.id]);

  useEffect(() => {
    api.get("/settings")
      .then(res => setEmailFollowupEnabled(res.data.email_followup_enabled || false))
      .catch(() => setEmailFollowupEnabled(false));
  }, []);

  const retryFollowup = async (stage) => {
    setRetrying(stage);
    try {
      const res = await api.post(`/contracts/${id}/retry-followup/${stage}`);
      const result = followUpResult(res.data);
      if (result.ok) toast.success(result.message);
      else toast.error(result.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  if (!contract) {
    return (
      <div className="p-8 text-center space-y-4">
        {loadingContract ? (
          <div className="text-sm font-bold text-[#6B7280]">Loading service contract details...</div>
        ) : (
          <div className="space-y-3">
            <div className="text-base font-bold text-[#111827]">Service Contract Not Found</div>
            <p className="text-xs text-[#6B7280]">This contract may have been deleted or you may not have permission to view it.</p>
            <Link to="/service-crm" className="inline-block bg-[#002FA7] text-white px-4 py-2 text-xs font-bold hover:bg-[#002280]">
              Back to Service CRM
            </Link>
          </div>
        )}
      </div>
    );
  }

  const assignedSp = salespeople.find(s => s.id === contract.assigned_to);
  const canActOnBehalf = user.role === "admin" || user.role === "manager";

  const handleFileUpload = async (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;
    setUploadingPdf(true);
    try {
      if (files.length === 1) {
        const formData = new FormData();
        formData.append("file", files[0]);
        const res = await api.post("/upload", formData);
        if (res.data?.url) {
          setAttachments(prev => [...prev, res.data.url]);
          toast.success(`Attached "${files[0].name}"`);
        }
      } else {
        const formData = new FormData();
        files.forEach(f => formData.append("files", f));
        const res = await api.post("/upload/multiple", formData);
        const newUrls = (res.data?.files || []).map(f => f.url);
        if (newUrls.length > 0) {
          setAttachments(prev => [...prev, ...newUrls]);
          toast.success(`${newUrls.length} files attached successfully`);
        }
      }
    } catch (err) {
      toast.error("File upload failed");
    } finally {
      setUploadingPdf(false);
      e.target.value = "";
    }
  };

  const send = async (type) => {
    if (sending) return;
    const isQuote = type === "quotation";
    const isPayRem = type === "payment_reminder";
    const channel = isQuote ? quoteChannel : isPayRem ? payChannel : (type === "whatsapp" || type === "email" ? type : null);
    if (!msg.trim() && type !== "note" && attachments.length === 0) return toast.error("Message is empty");
    if (channel === "whatsapp" && !String(contract?.customer_phone || "").replace(/\D/g, "")) {
      return toast.error("This contract has no valid phone number");
    }
    setSending(true);
    try {
      const payload = {
        contract_id: id,
        type,
        content: msg,
        subject: (type === "email" || (isQuote && quoteChannel === "email") || (isPayRem && payChannel === "email")) ? subject : "",
        attachments,
        is_quotation_send: isQuote,
        is_payment_reminder_send: isPayRem,
        channel
      };
      if (onBehalfOf) payload.on_behalf_of = onBehalfOf;
      const res = await api.post(`/contracts/${id}/send`, payload, { timeout: 60000 });
      if (res.data?.status === "failed") {
        toast.error(formatActivityError(res.data.error) || "Message was not sent");
        load();
        return;
      }
      toast.success(
        type === "note" ? "Note added" :
          isQuote ? `Quotation sent via ${quoteChannel.toUpperCase()} & follow-up sequence started!` :
          isPayRem ? `Payment reminder sent via ${payChannel.toUpperCase()} & payment follow-up sequence started!` :
            `${type === "whatsapp" ? "WhatsApp" : "Email"} sent`
      );
      setMsg(""); setSubject(""); setAttachments([]); load();
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.error;
      const timedOut = e.code === "ECONNABORTED" || String(e.message || "").toLowerCase().includes("timeout");
      const errMsg = timedOut
        ? "WhatsApp did not answer in time. The message was not sent. Check that WhatsApp shows Connected, then try again."
        : (detail || "Send failed");
      toast.error(formatActivityError(errMsg) || errMsg);
    } finally { setSending(false); }
  };

  const updateStatus = async (newStatus) => {
    let payload = { status: newStatus };
    if (newStatus === "converted") {
      const amountStr = window.prompt("Enter the product/deal amount for this conversion:");
      if (amountStr === null) return; // User cancelled
      const amount = parseFloat(amountStr);
      if (!isNaN(amount)) {
        payload.amount = amount;
      }
    }
    try {
      await api.patch(`/contracts/${id}`, payload);
      if (newStatus === "completed") {
        const ctype = (contract?.contract_type || "").toLowerCase();
        if (ctype === "warranty") {
          toast.success("Warranty marked completed! Automatically converted to Out of Warranty with 5 renewal follow-ups.");
        } else if (ctype === "amc") {
          toast.success("AMC marked completed! Automatically converted to Out of AMC with 5 renewal follow-ups.");
        } else {
          toast.success("Contract marked as completed");
        }
      } else {
        toast.success("Status updated");
      }
      load();
    } catch (e) { toast.error("Failed to update status"); }
  };

  const reassign = async (sp_id) => {
    try {
      await api.post(`/contracts/${id}/assign`, { salesperson_id: sp_id });
      toast.success("Reassigned");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteContract = async () => {
    if (!window.confirm("Are you sure you want to delete this contract?")) return;
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("Contract deleted");
      navigate("/contracts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete contract");
    }
  };

  const applyTemplate = (t) => {
    setMsg(t.body);
    if (t.subject) setSubject(t.subject);
    if (tab === "quotation") {
      if (t.type === "whatsapp" || t.type === "email") setQuoteChannel(t.type);
    } else if (tab === "payment_reminder") {
      if (t.type === "whatsapp" || t.type === "email") setPayChannel(t.type);
    } else if (tab !== "note") {
      setTab(t.type);
    }
    setAttachments(t.attachments || []);
  };

  const fillDefaultPaymentReminder = () => {
    const custName = (contract.customer_name || "there").split(" ")[0];
    const amountStr = contract.amount ? `₹${Number(contract.amount).toLocaleString('en-IN')}` : "";
    const cardStr = contract.card_no ? `(Card No: ${contract.card_no})` : "";
    const prodStr = contract.product_name || "service contract";
    setSubject(`Payment Reminder: Service Contract ${contract.card_no || ''} - ${prodStr}`);
    setMsg(`Hi ${custName}, this is a gentle reminder regarding the pending payment ${amountStr ? `of ${amountStr} ` : ''}for your ${prodStr} ${cardStr}. Please find the invoice/details attached and kindly let us know once the payment is completed. Thank you!`);
  };

  const channelTemplates = templates.filter(t => {
    if ((t.category || "lead") !== "service") return false;
    if (tab === "quotation") return t.stage === "quotation" || t.stage?.startsWith("qfu") || t.type === quoteChannel;
    if (tab === "payment_reminder") return t.stage === "payment_reminder" || t.stage?.startsWith("pfu") || t.type === payChannel;
    return t.type === tab;
  });

  const timelineActivities = activities.filter(a => a.type !== "note");
  const noteActivities = activities.filter(a => a.type === "note");

  const waQuoteAct = activities.find(a => (a.type === "quotation" || a.is_quotation_send) && a.channel === "whatsapp");
  const emailQuoteAct = activities.find(a => (a.type === "quotation" || a.is_quotation_send) && a.channel === "email");
  const waPayAct = activities.find(a => (a.type === "payment_reminder" || a.is_payment_reminder_send) && a.channel === "whatsapp");
  const emailPayAct = activities.find(a => (a.type === "payment_reminder" || a.is_payment_reminder_send) && a.channel === "email");

  const formatQuoteStatus = (act) => {
    if (!act) return "Not Sent";
    let s = act.status;
    if (s === "sent") return "Sent";
    if (s === "queued") return "Pending";
    if (s === "failed") return "Failed";
    return s;
  };

  let quotationStatusText = "Not Sent";
  if (contract.quotation_sent) {
    if (waQuoteAct || emailQuoteAct) {
      quotationStatusText = `WhatsApp: ${formatQuoteStatus(waQuoteAct)} | Email: ${formatQuoteStatus(emailQuoteAct)}`;
    } else {
      quotationStatusText = "Quotation Sent (Active)";
    }
  }

  return (
    <div data-testid="contract-detail-page">
      <Link to="/service-crm" className="text-xs font-bold uppercase tracking-widest text-[#6B7280] hover:text-[#002FA7] inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={12} weight="bold" /> Back to Service CRM
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6 pb-6 border-b border-[#E5E7EB]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{contract.customer_name || "Unknown Customer"}</h1>
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-gray-300 text-gray-700 bg-gray-50">
              {contract.status.toUpperCase()}
            </span>
          </div>
          <div className="text-sm text-[#6B7280] break-words">
            {contract.product_name || "—"} · {CONTRACT_TYPE_LABELS[contract.contract_type] || contract.contract_type || "—"} · {contract.customer_phone || "—"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {user?.role === "admin" && (
            <button onClick={deleteContract} data-testid="delete-contract-btn"
              className="border border-[#EF4444] text-[#EF4444] px-3 py-2 text-sm font-bold hover:bg-[#FEF2F2]">
              Delete
            </button>
          )}
          {contract.status === "active" && (contract.contract_type === "warranty" || contract.contract_type === "amc") && (
            <button
              onClick={() => updateStatus("completed")}
              data-testid="complete-convert-btn"
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-3 py-2 text-sm font-bold flex items-center gap-1.5 shadow-sm transition-colors"
              title={`Mark ${CONTRACT_TYPE_LABELS[contract.contract_type]} completed and convert to Out of ${contract.contract_type === 'warranty' ? 'Warranty' : 'AMC'}`}
            >
              <ArrowsClockwise size={15} weight="bold" />
              <span>Complete & Convert to Out of {contract.contract_type === "warranty" ? "Warranty" : "AMC"}</span>
            </button>
          )}
          <select value={contract.status} onChange={(e) => updateStatus(e.target.value)} data-testid="status-select"
            className="border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-bold w-full sm:w-auto">
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="renewed">Renewed</option>
            <option value="declined">Declined</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Contract info + composer */}
        <div className="lg:col-span-3 space-y-6">
          {/* Contract info */}
          <section className="border border-[#E5E7EB] p-5 bg-white">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280] mb-3">Service Details</div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <InfoRow label="Card No" value={contract.card_no || "—"} />
              <InfoRow label="Contract Type" value={CONTRACT_TYPE_LABELS[contract.contract_type] || contract.contract_type || "—"} />
              <InfoRow label="Assigned To" value={salespeople.find(s => s.id === contract.assigned_to)?.name || (contract.assigned_to ? "—" : "Unassigned")} />
              <InfoRow label="Phone / WhatsApp" value={contract.customer_phone || "—"} />
              <InfoRow label="Email" value={contract.customer_email || "—"} />
              <InfoRow label="Product" value={contract.product_name || "—"} />
              <InfoRow label="Amount" value={contract.amount ? `₹${Number(contract.amount).toLocaleString('en-IN')}` : "—"} />
              <InfoRow label="Area" value={contract.area || "—"} />
              <InfoRow label="PostCode" value={contract.postcode || "—"} />
              <InfoRow label="Start Date" value={contract.start_date ? new Date(contract.start_date).toLocaleDateString('en-IN') : "—"} />
              <InfoRow label="Expiry Date" value={contract.expiry_date ? new Date(contract.expiry_date).toLocaleDateString('en-IN') : "—"} />
              {contract.address && (
                <div className="col-span-1 sm:col-span-2">
                  <InfoRow label="Address" value={contract.address} />
                </div>
              )}
              {contract.notes && (
                <div className="col-span-1 sm:col-span-2 pt-3 mt-1 border-t border-[#F3F4F6]">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">Notes / Remarks</div>
                  <div className="text-sm font-sans mt-1.5 p-3 bg-[#F9FAFB] border border-[#E5E7EB] text-[#374151] whitespace-pre-wrap font-medium contracting-relaxed">
                    {contract.notes}
                  </div>
                </div>
              )}
            </dl>
            {canActOnBehalf && salespeople.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280] mb-1">Reassign</div>
                <select value={contract.assigned_to || ""} onChange={(e) => reassign(e.target.value)} data-testid="reassign-select"
                  className="border border-[#D1D5DB] px-3 py-2 text-sm bg-white w-full">
                  <option value="" disabled>Pick salesperson</option>
                  {salespeople.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
          </section>

          {/* Follow-up schedule */}
          <section className="border border-[#E5E7EB] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClockClockwise size={16} weight="bold" />
                <div className="text-[10px] font-bold uppercase tracking-widest">
                  {contract.payment_reminder_sent ? "Payment Reminder Follow-up Schedule (Active)" : contract.quotation_sent ? "Quotation Follow-up Schedule (Active)" : "General Follow-up Schedule"}
                </div>
              </div>
              {contract.payment_reminder_sent ? (
                <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                  Payment Reminder Active
                </span>
              ) : contract.quotation_sent ? (
                <span className="bg-purple-100 text-[#7C3AED] border border-purple-300 text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">
                  Quotation Flow Active
                </span>
              ) : null}
            </div>

            {contract.payment_reminder_sent && (
              <div className="mb-4 flex flex-col gap-2">
                <div className="text-xs text-emerald-900 bg-emerald-50 p-2.5 border border-emerald-200">
                  💳 Payment reminder sent on {contract.payment_reminder_sent_at ? new Date(contract.payment_reminder_sent_at).toLocaleDateString('en-IN') : 'recently'}.
                  Automated payment follow-up cadence (5-stage) is actively tracking this customer.
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm font-bold text-[#002FA7] bg-[#F3F4F6] p-2 border border-[#E5E7EB] inline-flex items-center gap-1.5 shadow-sm">
                    <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" />
                    Reminder sent via WhatsApp: {formatQuoteStatus(waPayAct)}
                  </div>
                  <div className="text-sm font-bold text-[#002FA7] bg-[#F3F4F6] p-2 border border-[#E5E7EB] inline-flex items-center gap-1.5 shadow-sm">
                    <EnvelopeSimple size={16} weight="fill" className="text-[#002FA7]" />
                    Reminder sent via Email: {formatQuoteStatus(emailPayAct)}
                  </div>
                </div>
              </div>
            )}

            {contract.quotation_sent && !contract.payment_reminder_sent && (
              <div className="mb-4 flex flex-col gap-2">
                <div className="text-xs text-[#6B7280] bg-purple-50 p-2 border border-purple-200">
                  ✨ Quotation sent on {contract.quotation_sent_at ? new Date(contract.quotation_sent_at).toLocaleDateString('en-IN') : 'recently'}.
                  Old general follow-ups have been automatically cancelled and replaced by this quotation follow-up cadence.
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm font-bold text-[#002FA7] bg-[#F3F4F6] p-2 border border-[#E5E7EB] inline-flex items-center gap-1.5 shadow-sm">
                    <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" />
                    Quotation sent via WhatsApp: {formatQuoteStatus(waQuoteAct)}
                  </div>
                  <div className="text-sm font-bold text-[#002FA7] bg-[#F3F4F6] p-2 border border-[#E5E7EB] inline-flex items-center gap-1.5 shadow-sm">
                    <EnvelopeSimple size={16} weight="fill" className="text-[#002FA7]" />
                    Quotation sent via Email: {formatQuoteStatus(emailQuoteAct)}
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up schedule according to Service Type */}
            {(() => {
              const stageGroups = (() => {
                if (contract.payment_reminder_sent) {
                  return [
                    {
                      title: "Payment Reminder Follow-up Schedule (Active)",
                      badge: "Payment Reminder Active",
                      badgeClass: "bg-emerald-100 text-emerald-800 border-emerald-300",
                      stages: [
                        { key: "pfu1", label: "Payment FU-1" },
                        { key: "pfu2", label: "Payment FU-2" },
                        { key: "pfu3", label: "Payment FU-3" },
                        { key: "pfu4", label: "Payment FU-4" },
                        { key: "pfu5", label: "Payment FU-5" },
                      ]
                    }
                  ];
                }
                if (contract.quotation_sent) {
                  return [
                    {
                      title: "Quotation Follow-up Schedule (Active)",
                      badge: "Quotation Flow Active",
                      badgeClass: "bg-purple-100 text-[#7C3AED] border-purple-300",
                      stages: [
                        { key: "qfu1", label: "Quote FU-1" },
                        { key: "qfu2", label: "Quote FU-2" },
                        { key: "qfu3", label: "Quote FU-3" },
                        { key: "qfu4", label: "Quote FU-4" },
                        { key: "qfu5", label: "Quote FU-5" },
                      ]
                    }
                  ];
                }

                const ctype = (contract.contract_type || "").toLowerCase().trim();

                if (ctype === "warranty" || ctype === "amc") {
                  return [
                    {
                      title: "After Start Check-ins (3 Follow-ups)",
                      description: "Scheduled after contract start (+7d, +30d, +90d)",
                      badge: "3 Follow-ups (After Start)",
                      badgeClass: "bg-blue-50 text-[#002FA7] border-blue-200",
                      gridClass: "grid-cols-1 sm:grid-cols-3",
                      stages: [
                        { key: "start_fu1", label: "Start FU-1" },
                        { key: "start_fu2", label: "Start FU-2" },
                        { key: "start_fu3", label: "Start FU-3" },
                      ]
                    },
                    {
                      title: "Before Expiry Renewal Reminders (5 Follow-ups)",
                      description: "Scheduled leading up to Expiry Date",
                      badge: "5 Follow-ups (Before Expiry)",
                      badgeClass: "bg-amber-50 text-amber-900 border-amber-300",
                      gridClass: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
                      stages: [
                        { key: "exp_fu1", label: "Renewal FU-1" },
                        { key: "exp_fu2", label: "Renewal FU-2" },
                        { key: "exp_fu3", label: "Renewal FU-3" },
                        { key: "exp_fu4", label: "Renewal FU-4" },
                        { key: "exp_fu5", label: "Renewal FU-5" },
                      ]
                    }
                  ];
                }

                if (ctype === "out_of_amc" || ctype === "out_of_warranty") {
                  return [
                    {
                      title: "Renewal & Conversion Follow-ups (5 Stages)",
                      description: "Follow-up cadence to convert / renew out-of-coverage service",
                      badge: "5 Follow-ups",
                      badgeClass: "bg-purple-50 text-[#7C3AED] border-purple-200",
                      gridClass: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
                      stages: [
                        { key: "fu1", label: "Follow-up 1" },
                        { key: "fu2", label: "Follow-up 2" },
                        { key: "fu3", label: "Follow-up 3" },
                        { key: "fu4", label: "Follow-up 4" },
                        { key: "fu5", label: "Follow-up 5" },
                      ]
                    }
                  ];
                }

                if (ctype === "on_call") {
                  return [
                    {
                      title: "On-Call Service Follow-ups (3 Stages)",
                      description: "3-stage follow-up cadence for on-call service requests",
                      badge: "3 Follow-ups",
                      badgeClass: "bg-emerald-50 text-emerald-800 border-emerald-200",
                      gridClass: "grid-cols-1 sm:grid-cols-3",
                      stages: [
                        { key: "fu1", label: "Follow-up 1" },
                        { key: "fu2", label: "Follow-up 2" },
                        { key: "fu3", label: "Follow-up 3" },
                      ]
                    }
                  ];
                }

                return [];
              })();

              if (stageGroups.length === 0) {
                return (
                  <div className="p-4 bg-gray-50 border border-gray-200 text-xs text-gray-600 flex items-center justify-between">
                    <div>
                      <span className="font-bold text-gray-800">No Automated Follow-ups Configured</span>
                      <p className="mt-0.5 text-[11px] text-gray-500">
                        {CONTRACT_TYPE_LABELS[contract.contract_type] || contract.contract_type} services do not have automated recurring follow-ups. You can still send manual WhatsApp, Email, Quotations, or Payment Reminders below at any time.
                      </p>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 bg-white border border-gray-300 text-gray-600">
                      0 Follow-ups
                    </span>
                  </div>
                );
              }

              return (
                <div className="space-y-6">
                  {stageGroups.map((group, gIdx) => (
                    <div key={gIdx} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-[11px] font-bold uppercase tracking-widest text-[#111827]">
                            {group.title}
                          </div>
                          {group.description && (
                            <div className="text-[11px] text-gray-500">{group.description}</div>
                          )}
                        </div>
                        {group.badge && (
                          <span className={`text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider border ${group.badgeClass || "bg-gray-100 text-gray-700 border-gray-200"}`}>
                            {group.badge}
                          </span>
                        )}
                      </div>

                      <div className={`grid gap-3 ${group.gridClass || (group.stages.length <= 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5")}`}>
                        {group.stages.map((stg) => {
                          const s = stg.key;
                          const due = contract[`${s}_due_at`];
                          const sent = contract[`${s}_sent_at`];
                          const status = contract[`${s}_status`];
                          const isFailed = status === "failed";
                          const isQueued = status === "queued";
                          const isCancelled = status === "cancelled";
                          const isPendingOrFailed = isFailed || isQueued;

                          return (
                            <div
                              key={s}
                              className={`border p-3 flex flex-col justify-between ${
                                contract.payment_reminder_sent
                                  ? "border-emerald-200 bg-emerald-50/20"
                                  : contract.quotation_sent
                                  ? "border-purple-200 bg-purple-50/20"
                                  : "border-[#E5E7EB] bg-white"
                              }`}
                              data-testid={`schedule-${s}`}
                            >
                              <div>
                                <div
                                  className={`text-[10px] font-bold uppercase tracking-widest ${
                                    contract.payment_reminder_sent
                                      ? "text-emerald-800"
                                      : contract.quotation_sent
                                      ? "text-[#7C3AED]"
                                      : "text-[#6B7280]"
                                  }`}
                                >
                                  {stg.label}
                                </div>
                                <div className="text-xs font-mono mt-1">
                                  {isFailed ? (
                                    <span className="text-[#EF4444] flex items-center gap-1">
                                      <Warning size={12} weight="bold" /> Failed
                                    </span>
                                  ) : isQueued ? (
                                    <span className="text-[#F59E0B] flex items-center gap-1">
                                      <ClockClockwise size={12} weight="bold" /> Queued
                                    </span>
                                  ) : isCancelled ? (
                                    <span className="text-[#9CA3AF] flex items-center gap-1">
                                      ✕ Stopped
                                    </span>
                                  ) : sent ? (
                                    <span className="text-[#10B981] flex flex-col gap-0.5">
                                      <span className="flex items-center gap-1 font-semibold">
                                        <CheckCircle size={12} weight="fill" /> Sent
                                      </span>
                                      <span className="text-[11px] text-[#374151] font-normal">
                                        {new Date(sent).toLocaleDateString()} {new Date(sent).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </span>
                                  ) : due ? (
                                    <span className="flex flex-col gap-0.5">
                                      <span className="text-[#6B7280] font-semibold">Due</span>
                                      <span className="text-[11px] text-[#374151] font-normal">
                                        {new Date(due).toLocaleDateString()} {new Date(due).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </div>
                              </div>
                              {isPendingOrFailed && (
                                <div className="mt-2 pt-2 border-t border-[#F3F4F6]">
                                  <button
                                    onClick={() => retryFollowup(s)}
                                    disabled={retrying === s}
                                    className="bg-[#002FA7] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 hover:bg-[#002280] disabled:opacity-60 transition-colors w-full text-center"
                                  >
                                    {retrying === s ? (isQueued ? "Sending..." : "Retrying...") : (isQueued ? "Send Now" : "Retry")}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </section>

          {/* Composer */}
          <section className="border border-[#E5E7EB] p-5">
            <div className="flex border-b border-[#E5E7EB] mb-4 -mx-5 px-5 overflow-x-auto">
              {[
                { k: "whatsapp", l: "WhatsApp", Icon: WhatsappLogo },
                { k: "email", l: "Email", Icon: EnvelopeSimple },
                { k: "quotation", l: "Send Quotation", Icon: FileText },
                { k: "payment_reminder", l: "Payment Reminder", Icon: CurrencyInr },
                { k: "note", l: "Note", Icon: Note },
              ].map(({ k, l, Icon }) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`compose-tab-${k}`}
                  className={`px-4 py-3 text-sm font-bold flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? (k === "quotation" ? "border-[#7C3AED] text-[#7C3AED]" : k === "payment_reminder" ? "border-emerald-600 text-emerald-700" : "border-[#002FA7] text-[#002FA7]") : "border-transparent text-[#6B7280] hover:text-[#111827]"
                    }`}>
                  <Icon size={16} weight="bold" /> {l}
                </button>
              ))}
            </div>

            {tab === "quotation" && (
              <div className="mb-4 p-3.5 bg-purple-50 border border-purple-200">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#6D28D9] mb-2.5">Quotation Delivery Channel</div>
                <div className="flex gap-6 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-[#111827]">
                    <input type="radio" name="quoteChannel" checked={quoteChannel === "whatsapp"} onChange={() => setQuoteChannel("whatsapp")} className="accent-[#7C3AED]" />
                    <WhatsappLogo size={18} weight="fill" className="text-green-600" /> Send via WhatsApp
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-[#111827]">
                    <input type="radio" name="quoteChannel" checked={quoteChannel === "email"} onChange={() => setQuoteChannel("email")} className="accent-[#7C3AED]" />
                    <EnvelopeSimple size={18} weight="fill" className="text-blue-600" /> Send via Email
                  </label>
                </div>
                <div className="text-[11px] text-[#6B7280]">
                  ⚡ Sending a quotation updates status to <b>Quotation Sent</b> and activates the automated quotation follow-up cadence while stopping general follow-ups.
                </div>
              </div>
            )}

            {tab === "payment_reminder" && (
              <div className="mb-4 p-3.5 bg-emerald-50 border border-emerald-200">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-800 flex items-center gap-1.5">
                    <CurrencyInr size={14} weight="bold" /> Payment Reminder Delivery Channel
                  </div>
                  <button
                    type="button"
                    onClick={fillDefaultPaymentReminder}
                    className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 bg-white border border-emerald-300 px-2 py-0.5 shadow-2xs hover:bg-emerald-100 transition-colors"
                  >
                    + Auto-Fill Message
                  </button>
                </div>
                <div className="flex gap-6 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-[#111827]">
                    <input type="radio" name="payChannel" checked={payChannel === "whatsapp"} onChange={() => setPayChannel("whatsapp")} className="accent-emerald-600" />
                    <WhatsappLogo size={18} weight="fill" className="text-green-600" /> Send via WhatsApp
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-[#111827]">
                    <input type="radio" name="payChannel" checked={payChannel === "email"} onChange={() => setPayChannel("email")} className="accent-emerald-600" />
                    <EnvelopeSimple size={18} weight="fill" className="text-blue-600" /> Send via Email
                  </label>
                </div>
                <div className="text-[11px] text-emerald-900 flex flex-wrap items-center gap-2 pt-1 border-t border-emerald-200/60 mt-2">
                  <span><b>Amount:</b> {contract.amount ? `₹${Number(contract.amount).toLocaleString('en-IN')}` : "Not Set"}</span>
                  <span>•</span>
                  <span><b>Card No:</b> {contract.card_no || "—"}</span>
                  <span>•</span>
                  <span><b>Client:</b> {contract.customer_name || "—"}</span>
                </div>
                <div className="text-[11px] text-emerald-800 mt-1.5">
                  ⚡ Sending a payment reminder updates status to <b>Payment Reminder Sent</b> and activates the automated 5-step payment follow-up cadence while cancelling general follow-ups.
                </div>
              </div>
            )}

            {canActOnBehalf && tab !== "note" && (
              <div className="mb-3">
                <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">Send on behalf of (manager)</label>
                <select value={onBehalfOf} onChange={(e) => setOnBehalfOf(e.target.value)} data-testid="on-behalf-select"
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white mt-1">
                  <option value="">Default ({assignedSp?.name || "auto"})</option>
                  {salespeople.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}

            {(tab === "email" || (tab === "quotation" && quoteChannel === "email") || (tab === "payment_reminder" && payChannel === "email")) && (
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (e.g. Payment Reminder / Invoice for contract...)"
                data-testid="compose-subject"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm mb-2 focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            )}
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5}
              data-testid="compose-body"
              placeholder={tab === "note" ? "Add an internal team note..." : tab === "quotation" ? `Type your quotation message. Select a template below or attach a manual PDF quotation.` : tab === "payment_reminder" ? `Type your payment reminder message, or click '+ Auto-Fill Message' above.` : `Type your ${tab} message. You can use {name}, {product}, {company}, {card_no}, {amount}, {salesperson}.`}
              className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />

            {tab !== "note" && (
              <div className="mt-2 flex items-center justify-between bg-gray-50 p-2.5 border border-[#E5E7EB]">
                <div className="flex items-center gap-3">
                  <label className="cursor-pointer bg-white border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold text-[#374151] hover:bg-gray-100 flex items-center gap-1.5 shadow-sm transition-all hover:border-[#002FA7]">
                    <UploadSimple size={14} weight="bold" className="text-[#002FA7]" />
                    {uploadingPdf ? "Uploading..." : "Attach Documents / Media"}
                    <input
                      type="file"
                      multiple
                      accept=".pdf,application/pdf,image/*,video/*,.mp4,.mov,.webm,.3gp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                      onChange={handleFileUpload}
                      disabled={uploadingPdf}
                      className="hidden"
                    />
                  </label>
                  <span className="text-[11px] text-[#6B7280]">
                    {tab === "payment_reminder" ? "Attach invoice, receipt, QR code, or proof media" : tab === "quotation" ? "Attach service quotation PDF, demo videos or documents" : "Attach multiple PDFs, images, demo videos, or documents"}
                  </span>
                </div>
                {attachments.length > 0 && <span className="text-xs font-bold text-[#10B981]">{attachments.length} attached</span>}
              </div>
            )}

            {attachments.length > 0 && tab !== "note" && (
              <div className="mt-2">
                <DocumentAttachmentList
                  attachments={attachments}
                  onRemove={(idx) => setAttachments(attachments.filter((_, i) => i !== idx))}
                />
              </div>
            )}

            {tab !== "note" && (
              <div className="mt-3 mb-2">
                <select
                  onChange={(e) => {
                    const t = channelTemplates.find(x => x.id === e.target.value);
                    if (t) applyTemplate(t);
                    e.target.value = "";
                  }}
                  disabled={channelTemplates.length === 0}
                  className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none disabled:bg-[#F3F4F6] disabled:text-[#6B7280]"
                >
                  <option value="">
                    {channelTemplates.length > 0
                      ? `Insert a ${tab === "quotation" ? quoteChannel : tab === "payment_reminder" ? payChannel : tab} template...`
                      : `No ${tab} templates available`}
                  </option>
                  {channelTemplates.map(t => (
                    <option key={t.id} value={t.id}>
                      {t.name} (Service · {SERVICE_STAGE_LABELS[t.stage] || t.stage})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button onClick={() => send(tab)} disabled={sending || uploadingPdf} data-testid="compose-send-button"
                className={`${tab === "quotation" ? "bg-[#7C3AED] hover:bg-[#6D28D9]" : tab === "payment_reminder" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-[#002FA7] hover:bg-[#002280]"} text-white font-bold px-5 py-2.5 flex items-center gap-2 disabled:opacity-60 transition-colors`}>
                <PaperPlaneTilt size={16} weight="bold" />
                {sending ? "Sending..." : tab === "note" ? "Save Note" : tab === "quotation" ? `Send Quotation via ${quoteChannel === "whatsapp" ? "WhatsApp" : "Email"}` : tab === "payment_reminder" ? `Send Payment Reminder via ${payChannel === "whatsapp" ? "WhatsApp" : "Email"}` : `Send ${tab === "whatsapp" ? "WhatsApp" : "Email"}`}
              </button>
            </div>
          </section>
        </div>

        {/* Right: Notes & Timeline */}
        <aside className="lg:col-span-2 space-y-6">
          {/* Dedicated Notes Section */}
          <div className="border border-[#E5E7EB] p-5 bg-amber-50/40 shadow-xs">
            <div className="flex items-center gap-2 mb-3.5">
              <Note size={18} weight="bold" className="text-amber-600" />
              <h3 className="font-heading font-bold text-lg text-[#111827]">
                Internal Team Notes ({noteActivities.length + (contract.notes ? 1 : 0)})
              </h3>
            </div>
            {noteActivities.length === 0 && !contract.notes ? (
              <div className="text-sm text-[#6B7280] py-5 text-center border border-dashed border-[#D1D5DB] bg-white">
                No notes recorded. Use the "Note" tab below to add internal team notes for this contract.
              </div>
            ) : (
              <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1">
                {contract.notes && (
                  <div className="p-3.5 bg-white border-l-4 border-amber-500 border-y border-r border-[#E5E7EB] text-sm">
                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-1.5">
                      <span>Initial Note (From Import / Creation)</span>
                      <span>{new Date(contract.created_at).toLocaleDateString('en-IN')}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-[#374151] font-sans">{contract.notes}</p>
                  </div>
                )}
                {noteActivities.map(n => {
                  const sp = salespeople.find(s => s.id === n.user_id);
                  return (
                    <div key={n.id} className="p-3.5 bg-white border border-[#E5E7EB] text-sm shadow-2xs">
                      <p className="whitespace-pre-wrap text-[#374151] mb-2 font-sans">{n.content}</p>
                      <div className="flex items-center justify-between text-[10px] text-[#6B7280] font-mono border-t border-gray-100 pt-2">
                        <span>{new Date(n.timestamp).toLocaleString('en-IN')}</span>
                        {sp && <span className="font-sans font-semibold text-[#111827]">by {sp.name}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div className="border border-[#E5E7EB] p-5 lg:sticky lg:top-4 bg-white">
            <h3 className="font-heading font-bold text-xl mb-4">Activity Timeline</h3>
            {timelineActivities.length === 0 ? (
              <div className="text-sm text-[#6B7280] py-6 text-center">No communication activity yet. Send your first message or quotation.</div>
            ) : (
              <div>
                <div className="lp-timeline pl-8 space-y-4">
                  {(showAllActivities ? timelineActivities : timelineActivities.slice(0, 5)).map(a => (
                    <TimelineItem
                      key={a.id}
                      a={a}
                      salespeople={salespeople}
                      onRetry={retryFollowup}
                      whatsAppConnected={whatsAppConnected}
                      emailFollowupEnabled={emailFollowupEnabled}
                      retrying={retrying}
                    />
                  ))}
                </div>
                {timelineActivities.length > 5 && (
                  <div className="mt-6 pt-4 border-t border-[#E5E7EB] flex justify-center">
                    <button
                      onClick={() => setShowAllActivities(!showAllActivities)}
                      className="text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:bg-[#F3F4F6] px-4 py-2 border border-[#E5E7EB] transition-colors"
                    >
                      {showAllActivities ? "Show Less Activities" : `View All Activities (${timelineActivities.length})`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">{label}</div>
      <div className="text-sm font-mono mt-0.5">{value}</div>
    </div>
  );
}

function TimelineItem({ a, salespeople, onRetry, whatsAppConnected, emailFollowupEnabled, retrying }) {
  const [expanded, setExpanded] = useState(false);
  const sp = salespeople.find(s => s.id === a.user_id);
  const onBehalfSp = salespeople.find(s => s.id === a.on_behalf_of);
  const isStatusChange = a.type === "status_change";
  const Icon = a.type === "whatsapp" ? WhatsappLogo : a.type === "email" ? EnvelopeSimple : a.type === "quotation" ? FileText : a.type === "follow_up_auto" ? ClockClockwise : isStatusChange ? ArrowsClockwise : Note;
  
  const statusBadge = isStatusChange
    ? { c: "#7C3AED", t: "Converted / Status" }
    : {
        sent: { c: "#10B981", t: "Sent" },
        simulated: { c: "#3B82F6", t: "Simulated" },
        queued: { c: "#F59E0B", t: "Queued" },
        failed: { c: "#EF4444", t: "Failed" },
      }[a.status] || { c: "#6B7280", t: a.status };

  const isFollowUpFailed = a.type === "follow_up_auto" && a.status === "failed";
  const isFollowUpQueued = a.type === "follow_up_auto" && a.status === "queued";
  const isFollowUpPendingOrFailed = isFollowUpFailed || isFollowUpQueued;
  const isLongContent = (a.content || "").length > 120 || (a.content || "").split("\n").length > 2;

  return (
    <div className="relative" data-testid={`activity-${a.id}`}>
      <div className="lp-timeline-dot" style={{ background: isStatusChange ? "#7C3AED" : a.status === "failed" ? "#EF4444" : "#002FA7" }} />
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} weight="bold" className={isStatusChange ? "text-[#7C3AED]" : ""} />
        <span className={`text-xs font-bold uppercase tracking-widest ${isStatusChange ? "text-[#7C3AED]" : ""}`}>
          {isStatusChange ? "SERVICE STATUS / CONVERSION" : a.type.replace("_", " ")}
          {a.follow_up_stage ? ` · ${a.follow_up_stage.toUpperCase()}` : ""}
          {a.channel && !isStatusChange ? ` · ${a.channel.toUpperCase()}` : ""}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 border"
          style={{ color: statusBadge.c, borderColor: statusBadge.c }}>{statusBadge.t}</span>
      </div>
      {a.subject && <div className="text-sm font-bold">{a.subject}</div>}
      {isStatusChange ? (
        <div className="mt-1 p-2.5 bg-purple-50/80 border border-purple-200 text-sm font-semibold text-purple-900 rounded-xs flex items-center gap-2">
          <ArrowsClockwise size={16} weight="bold" className="text-[#7C3AED] shrink-0" />
          <span>{a.content}</span>
        </div>
      ) : (
        <div className="mt-1">
          <p className={`text-sm text-[#374151] whitespace-pre-wrap ${!expanded && isLongContent ? 'line-clamp-2 overflow-hidden text-ellipsis' : ''}`}>
            {a.content}
          </p>
          {isLongContent && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-[#002FA7] font-bold hover:underline mt-1 focus:outline-none block"
            >
              {expanded ? "Show Less Details ▲" : "View Full Details ▼"}
            </button>
          )}
        </div>
      )}
      {a.attachments && a.attachments.length > 0 && (
        <div className="mt-2">
          <DocumentAttachmentList attachments={a.attachments} showThumbnails={false} />
        </div>
      )}
      <div className="text-[10px] text-[#6B7280] font-mono mt-1">
        {new Date(a.timestamp).toLocaleString('en-IN')}
        {onBehalfSp && sp && <> · {sp.name} acting as <b>{onBehalfSp.name}</b></>}
      </div>
      {a.error && <div className={`text-[10px] ${a.status === "queued" ? "text-[#D97706]" : "text-[#EF4444]"} mt-1 flex items-center gap-1`}><Warning size={10} weight="fill" />{formatActivityError(a.error, sp?.name || onBehalfSp?.name)}</div>}
      {isFollowUpPendingOrFailed && (
        <div className="mt-2">
          <button
            onClick={() => onRetry(a.follow_up_stage)}
            disabled={retrying === a.follow_up_stage}
            className="bg-[#002FA7] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 hover:bg-[#002280] disabled:opacity-60 transition-colors"
          >
            {retrying === a.follow_up_stage ? (isFollowUpQueued ? "Sending..." : "Retrying...") : (isFollowUpQueued ? "Send Now" : "Retry Follow-up")}
          </button>
        </div>
      )}
    </div>
  );
}
